// src/features/service-providers/ServiceProvidersPage.js
import React, { useEffect, useMemo, useState } from "react";
import "./serviceProviders.css";
import {
  fetchMetros,
  fetchCategories,
  fetchServicesForCategory,
  fetchProviders,
} from "./api";

function Stars({ value = 0, count = 0 }) {
  const full = Math.floor(value || 0);
  const half = value - full >= 0.5 ? 1 : 0;
  const empty = Math.max(0, 5 - full - half);
  const stars = [
    ...Array(full).fill("★"),
    ...Array(half).fill("☆"),
    ...Array(empty).fill("✩"),
  ].join("");
  return (
    <span className="sp-stars" title={`${value?.toFixed?.(1) ?? "N/A"} (${count || 0})`}>
      {stars} {value?.toFixed?.(1) ?? "N/A"} ({count || 0})
    </span>
  );
}

export default function ServiceProvidersPage({ onBack }) {
  const [metros, setMetros] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);

  const [metro, setMetro] = useState("");
  const [category, setCategory] = useState("");
  const [service, setService] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initial loads
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      const [ms, cs] = await Promise.all([fetchMetros(), fetchCategories()]);
      if (!mounted) return;
      setMetros(ms);
      setCategories(cs);
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  // Load services when category changes
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!category) {
        setServices([]);
        setService("");
        return;
      }
      const svc = await fetchServicesForCategory(category);
      if (!mounted) return;
      setServices(svc);
      setService("");
    })();
    return () => { mounted = false; };
  }, [category]);

  // Fetch providers when metro or category changes
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      const data = await fetchProviders({ metro, category });
      if (!mounted) return;
      setRows(data);
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, [metro, category]);

  const viewRows = useMemo(() => {
    if (!metro || !category) return [];
    return rows;
  }, [rows, metro, category]);

  return (
    <div className="sp-page">
      <div className="sp-header">
        <div className="sp-title">Get a Service Quote at a Speed Light</div>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              padding: "8px 12px",
              borderRadius: 10,
              border: "1px solid #d6dde8",
              background: "#fff",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(20,40,80,0.06)",
            }}
          >
            ← Back
          </button>
        )}
      </div>

      <div className="sp-sub">
        Choose a <strong>Metro</strong>, then a <strong>Category</strong>. Optionally pick a
        specific <strong>Service</strong> to see that quote column.
      </div>
{/* Filters panel */}
<div className="sp-panel">
  <div className="sp-filters">
    <select value={metro} onChange={(e) => setMetro(e.target.value)}>
      <option value="">Select Metro…</option>
      {metros.map((m) => (
        <option key={m} value={m}>{m}</option>
      ))}
    </select>

    <select
      value={category}
      onChange={(e) => setCategory(e.target.value)}
      className={!metro ? "sp-disabled" : ""}
      disabled={!metro}
    >
      <option value="">{metro ? "Select Category…" : "Select Metro first"}</option>
      {categories.map((c) => (
        <option key={c} value={c}>{c}</option>
      ))}
    </select>

    <select
      value={service}
      onChange={(e) => setService(e.target.value)}
      className={!category ? "sp-disabled" : ""}
      disabled={!category}
    >
      <option value="">{category ? "Optional: Specific Service…" : "Select Category first"}</option>
      {services.map((s) => (
        <option key={s} value={s}>{s}</option>
      ))}
    </select>
  </div>
</div>

      {/* Results table */}
      <div className="sp-table-wrap">
        <table className="sp-table">
          <thead>
            <tr>
              <th>Metro</th>
              <th>Category</th>
              <th>Firm</th>
              <th>Website</th>
              <th>Contact</th>
              <th>Google Rating</th>
              <th>{service ? `Quote: ${service}` : "Quotes (available services)"}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7}>Loading…</td></tr>
            ) : viewRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="sp-empty">
                  {(!metro || !category)
                    ? "Please select a Metro and Category to view providers."
                    : "No providers found for your selection."}
                </td>
              </tr>
            ) : (
              viewRows.map((r, i) => (
                <tr key={`${r.metro}-${r.firm}-${i}`}>
                  <td>{r.metro}</td>
                  <td>{r.category}</td>
                  <td>{r.firm}</td>
                  <td>
                    {r.website ? (
                      <a href={r.website} target="_blank" rel="noreferrer">
                        {(() => {
                          try { return new URL(r.website).hostname.replace(/^www\./, ""); }
                          catch { return r.website; }
                        })()}
                      </a>
                    ) : "—"}
                  </td>
                  <td>{r.contact || "—"}</td>
                  <td><Stars value={r.rating} count={r.ratingCount} /></td>
                  <td>
                    {service
                      ? (r.quotes?.[service] ?? "—")
                      : Object.entries(r.quotes || {}).map(([k, v]) => (
                          <span key={k} className="sp-chip">{k}: {v}</span>
                        ))
                    }
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
